import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Plus as IcPlus, Trash2 as IcTrash, Upload as IcUpload, X as IcX } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CRIMSON = '#8B1A1A';
const PAGE = 100;

const REASONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complaint', label: 'Complaint' },
];

const reasonColour = (r) => ({
  unsubscribed: '#a16207', bounced: '#b91c1c', complaint: '#b91c1c', manual: '#666',
}[r] || '#666');

export default function Suppressions() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');

  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('manual');
  const [adding, setAdding] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkReason, setBulkReason] = useState('manual');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchRows = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (reasonFilter) params.set('reason', reasonFilter);
      params.set('limit', PAGE);
      params.set('offset', offset);
      const res = await fetch(`${API_BASE}/clients/${clientId}/suppressions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, getToken, query, reasonFilter, offset]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setOffset(0); }, [query, reasonFilter]);

  const addOne = useCallback(async () => {
    const email = newEmail.trim();
    if (!email) return;
    setAdding(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/suppressions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, reason: newReason }),
      });
      if (!res.ok) throw new Error(`Add failed (${res.status})`);
      const data = await res.json();
      if (!data.added) setError(`${email} is already suppressed.`);
      setNewEmail('');
      await fetchRows();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  }, [newEmail, newReason, clientId, getToken, fetchRows]);

  const runBulk = useCallback(async () => {
    const emails = bulkText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!emails.length) { setError('Paste at least one email.'); return; }
    setBulkBusy(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/suppressions/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emails, reason: bulkReason }),
      });
      if (!res.ok) throw new Error(`Bulk failed (${res.status})`);
      const data = await res.json();
      setBulkResult(`Added ${data.added} of ${data.submitted} (duplicates skipped).`);
      setBulkText('');
      await fetchRows();
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkBusy(false);
    }
  }, [bulkText, bulkReason, clientId, getToken, fetchRows]);

  const remove = useCallback(async (row) => {
    if (!window.confirm(`Remove ${row.email} from suppressions?`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/suppressions/${row.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await fetchRows();
    } catch (e) {
      setError(e.message);
    }
  }, [getToken, fetchRows]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#111' }}>Suppressions</h1>
          <p style={{ color: '#666', marginTop: 6, marginBottom: 20, fontSize: 14 }}>
            Addresses that will never be emailed from this workspace.
          </p>
        </div>
        {clientId && (
          <button
            type="button"
            onClick={() => { setBulkOpen(true); setBulkResult(null); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', border: '1px solid #E2E2E2', borderRadius: 8, cursor: 'pointer', background: '#fff', color: '#444', fontSize: 14, fontWeight: 600 }}
          >
            <IcUpload size={16} /> Bulk add
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: '#FDEDED', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {isGlobalMode || !clientId ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999', border: '1px dashed #DDD', borderRadius: 12 }}>
          Select a client workspace to manage its suppressions.
        </div>
      ) : (
        <div style={{ maxWidth: 900 }}>
          {/* Add single */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addOne(); }}
              placeholder="email@example.com"
              style={{ flex: 1, padding: '9px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            />
            <select
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              style={{ padding: '9px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14 }}
            >
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <button
              type="button"
              onClick={addOne}
              disabled={adding}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', border: 'none', borderRadius: 8, cursor: adding ? 'default' : 'pointer', background: CRIMSON, color: '#fff', fontSize: 14, fontWeight: 600, opacity: adding ? 0.6 : 1 }}
            >
              <IcPlus size={16} /> Add
            </button>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search email…"
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            />
            <select
              value={reasonFilter}
              onChange={(e) => setReasonFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14 }}
            >
              <option value="">All reasons</option>
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
            {total} suppressed {total === 1 ? 'address' : 'addresses'}
          </div>

          {loading ? (
            <p style={{ color: '#999', fontSize: 14 }}>Loading…</p>
          ) : rows.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999', border: '1px dashed #DDD', borderRadius: 12 }}>
              {total === 0 ? 'No suppressed addresses.' : 'No matches.'}
            </div>
          ) : (
            <div style={{ border: '1px solid #EEE', borderRadius: 12, overflow: 'hidden' }}>
              {rows.map((row) => (
                <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #F2F2F2' }}>
                  <div style={{ flex: 1, fontSize: 14, color: '#222' }}>{row.email}</div>
                  <span style={{ fontSize: 12, color: reasonColour(row.reason), background: '#F7F7F7', padding: '2px 9px', borderRadius: 6, textTransform: 'capitalize' }}>
                    {row.reason}
                  </span>
                  {row.source && (
                    <span style={{ fontSize: 11, color: '#aaa' }}>{row.source}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(row)}
                    title="Remove from suppressions"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, border: 'none', borderRadius: 7, cursor: 'pointer', background: 'transparent', color: '#b91c1c' }}
                  >
                    <IcTrash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > PAGE && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
                disabled={offset === 0}
                style={{ padding: '7px 14px', border: '1px solid #E2E2E2', borderRadius: 8, cursor: offset === 0 ? 'default' : 'pointer', background: '#fff', fontSize: 14, opacity: offset === 0 ? 0.5 : 1 }}
              >Previous</button>
              <span style={{ fontSize: 13, color: '#888' }}>
                {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
              </span>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE)}
                disabled={offset + PAGE >= total}
                style={{ padding: '7px 14px', border: '1px solid #E2E2E2', borderRadius: 8, cursor: offset + PAGE >= total ? 'default' : 'pointer', background: '#fff', fontSize: 14, opacity: offset + PAGE >= total ? 0.5 : 1 }}
              >Next</button>
            </div>
          )}
        </div>
      )}

      {/* Bulk modal */}
      {bulkOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 520, maxWidth: '90vw' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: '#111' }}>Bulk add suppressions</h2>
              <button type="button" onClick={() => setBulkOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#888' }}><IcX size={20} /></button>
            </div>
            <p style={{ fontSize: 13, color: '#666', marginTop: 0, marginBottom: 12 }}>
              Paste emails separated by commas, spaces, or new lines.
            </p>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={8}
              placeholder="one@example.com, two@example.com…"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'ui-monospace, monospace' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <select value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14 }}>
                {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <button
                type="button"
                onClick={runBulk}
                disabled={bulkBusy}
                style={{ padding: '9px 18px', border: 'none', borderRadius: 8, cursor: bulkBusy ? 'default' : 'pointer', background: CRIMSON, color: '#fff', fontSize: 14, fontWeight: 600, opacity: bulkBusy ? 0.6 : 1 }}
              >
                {bulkBusy ? 'Adding…' : 'Add all'}
              </button>
              {bulkResult && <span style={{ fontSize: 13, color: '#1a7f3c' }}>{bulkResult}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
